import * as React from "react"

import { cn } from "@/lib/utils"
import { capitalizeWords } from "@/lib/autoCap"

/**
 * Textarea — same auto-capitalise behaviour as `<Input>` (see input.jsx
 * for the full doc). Multiline text especially benefits from Title Case
 * on address / product-description entry.
 *
 * Bypass keys: hold **Shift** for a single-character opt-out, or turn on
 * **Caps Lock** for a full-uppercase entry.
 */
const Textarea = React.forwardRef(
  ({ className, autoCap = true, onChange, onKeyDown, ...props }, ref) => {
    const capsRef = React.useRef(false)
    const shiftRef = React.useRef(false)

    const handleKeyDown = (e) => {
      if (typeof e.getModifierState === "function") {
        capsRef.current = e.getModifierState("CapsLock")
      }
      shiftRef.current = !!e.shiftKey
      onKeyDown?.(e)
    }

    const handleChange = (e) => {
      if (autoCap && !capsRef.current && !shiftRef.current) {
        const next = capitalizeWords(e.target.value)
        if (next !== e.target.value) {
          e.target.value = next
        }
      }
      shiftRef.current = false
      onChange?.(e)
    }

    return (
      <textarea
        className={cn(
          "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        onChange={autoCap ? handleChange : onChange}
        onKeyDown={autoCap ? handleKeyDown : onKeyDown}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
